
import { useState } from 'react';
import { Shield, BookOpen } from 'lucide-react';
import { RolesTable } from '@/components/permissions/RolesTable';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { useRoles, CustomRole } from '@/hooks/useRoles';
import { RoleCard } from '@/components/permissions/RoleCard';
import { Button } from '@/components/ui/button';
import { RoleDialog } from '@/components/permissions/RoleDialog';
import { AssignRoleDialog } from '@/components/permissions/AssignRoleDialog';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useEnhancedPermissions } from '@/hooks/useEnhancedPermissions';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PermissionDocs } from '@/components/permissions/PermissionDocs';
import { SecurityOverview } from '@/components/permissions/SecurityOverview';

export default function Permissions() {
  const [view, setView] = useState<'table' | 'cards'>('table');
  const { data: roles, isLoading } = useRoles();
  const [selectedRole, setSelectedRole] = useState<CustomRole | undefined>();
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const { isSystemAdmin } = useEnhancedPermissions();
  const { user } = useAuth();

  const handleEditRole = (role: CustomRole) => {
    setSelectedRole(role);
    setIsRoleDialogOpen(true);
  };

  const handleAssignRole = (role: CustomRole) => {
    setSelectedRole(role);
    setIsAssignDialogOpen(true);
  };

  const handleNewRole = () => {
    setSelectedRole(undefined);
    setIsRoleDialogOpen(true);
  };
  
  return (
    <ProtectedRoute requiredPermission="إدارة الصلاحيات">
      <div className="container mx-auto p-4 md:p-6 max-w-7xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">إدارة الصلاحيات والأمان</h1>
            <p className="text-gray-600">إدارة الأدوار، الصلاحيات، والمراقبة الأمنية</p>
          </div>
        </div>
        
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <Shield className="w-4 h-4" />
              نظرة عامة
            </TabsTrigger>
            <TabsTrigger value="roles" className="gap-2">
              <Shield className="w-4 h-4" />
              الأدوار المخصصة
            </TabsTrigger>
            <TabsTrigger value="docs" className="gap-2">
              <BookOpen className="w-4 h-4" />
              الدليل الشامل
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <SecurityOverview />
          </TabsContent>

          <TabsContent value="roles" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">قائمة الأدوار المخصصة</h2>
              <div className="flex items-center gap-4">
                <ViewToggle view={view} onViewChange={setView} />
                <Button onClick={handleNewRole}>
                  إنشاء دور جديد
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center p-4">جاري التحميل...</div>
            ) : view === 'table' ? (
              <RolesTable roles={roles} onEditRole={handleEditRole} onAssignRole={handleAssignRole} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles?.map(role => (
                  <RoleCard key={role.id} role={role} onEdit={handleEditRole} onAssign={handleAssignRole} />
                ))}
              </div>
            )}

            {roles?.length === 0 && !isLoading && (
              <div className="text-center py-12 text-gray-500">
                <p>لم يتم العثور على أي أدوار. حاول إنشاء دور جديد.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="docs">
            <PermissionDocs />
          </TabsContent>
        </Tabs>

        <RoleDialog
          open={isRoleDialogOpen}
          onOpenChange={setIsRoleDialogOpen}
          role={selectedRole}
        />

        <AssignRoleDialog
          open={isAssignDialogOpen}
          onOpenChange={setIsAssignDialogOpen}
          role={selectedRole}
        />
        
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4">معلومات المستخدم الحالي</h2>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-blue-700">المستخدم:</span> 
                  <span className="text-gray-800">{user?.username}</span>
                </p>
                <p className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-blue-700">الدور:</span> 
                  <span className="text-gray-800">{user?.role}</span>
                </p>
                <p className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-blue-700">نوع المستخدم:</span> 
                  <span className={`px-2 py-1 rounded-md text-sm ${isSystemAdmin ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                    {isSystemAdmin ? 'مدير النظام' : 'مستخدم عادي'}
                  </span>
                </p>
              </div>
              <div>
                <p className="font-semibold text-blue-700 mb-2">الصلاحيات:</p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {Array.isArray(user?.permissions) ? (
                    user.permissions.length > 0 ? (
                      user.permissions.map((perm, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm border border-blue-300">
                          {perm}
                        </span>
                      ))
                    ) : <span className="text-gray-500 italic">لا توجد صلاحيات محددة</span>
                  ) : (
                    <span className={`px-3 py-1 rounded-md text-sm font-medium ${user?.permissions === 'ALL' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-gray-100 text-gray-800 border border-gray-300'}`}>
                      {user?.permissions === 'ALL' ? 'جميع الصلاحيات' : 'لا توجد صلاحيات'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
